import React, { useEffect, useState } from "react";
import { auth } from "@/config/firebase";
import { Card, CardContent } from "@/components/ui/card";
import { Coins, Music, Sparkles, Cloud } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const SEASON = "2026-27";

export default function MeinAbend() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${API}/api/market-ops/my-evening?season_id=${SEASON}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(await res.json());
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0a0608] text-white/40 flex items-center justify-center">Lädt…</div>;
  if (!data) return <div className="min-h-screen bg-[#0a0608] text-white/40 flex items-center justify-center">Keine Daten.</div>;

  const pres = data.presentation || {};

  return (
    <div className="min-h-screen bg-[#0a0608] text-white p-4 md:p-8" data-testid="mein-abend-page">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-4">
          {data.photoUrl ? <img src={data.photoUrl} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-amber-500/40" /> :
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center text-2xl font-black text-amber-300">{(data.displayName || "?")[0]}</div>}
          <div>
            <h1 className="text-3xl font-black text-amber-400">Mein Abend</h1>
            <p className="text-white/60">{data.displayName}</p>
          </div>
        </div>

        {/* Coins */}
        <Card className="bg-gradient-to-r from-amber-600/20 to-yellow-600/5 border-amber-500/30">
          <CardContent className="p-4 flex items-center justify-around text-center">
            <div><p className="text-xs text-white/50">Guthaben</p><p className="text-2xl font-black text-amber-300 flex items-center gap-1 justify-center"><Coins size={20} />{Math.round(data.wallet.balance)}</p></div>
            <div><p className="text-xs text-white/50">Frei</p><p className="text-2xl font-black">{Math.round(data.wallet.available)}</p></div>
            <div><p className="text-xs text-white/50">Reserviert</p><p className="text-2xl font-black text-white/60">{Math.round(data.wallet.reserved)}</p></div>
          </CardContent>
        </Card>

        {/* Prediction image + song */}
        {(pres.backgroundImageUrl || pres.songUrl) && (
          <Card className="bg-white/5 border-white/10 overflow-hidden">
            {pres.backgroundImageUrl && <img src={pres.backgroundImageUrl} alt="" className="w-full max-h-72 object-cover" />}
            {pres.songUrl && (
              <CardContent className="p-4">
                <p className="text-sm text-white/60 flex items-center gap-2 mb-2"><Music size={16} /> Dein Song</p>
                <audio src={pres.songUrl} controls className="w-full" />
              </CardContent>
            )}
          </Card>
        )}

        {/* Own predictions */}
        <div>
          <h2 className="text-lg font-bold text-amber-300 flex items-center gap-2 mb-2"><Sparkles size={18} /> Deine Predictions</h2>
          <div className="space-y-2">
            {data.predictions.length === 0 ? <p className="text-white/40 text-sm">Keine Predictions.</p> : data.predictions.map((p) => (
              <Card key={p.id} className="bg-white/5 border-white/10">
                <CardContent className="p-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${p.type === "on_court" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"}`}>{p.type === "on_court" ? "ON-COURT" : "OFF-COURT"}</span>
                  <p className="text-white mt-1">{p.text}</p>
                  <p className="text-xs text-white/50 mt-1">Team: {p.teamProbability}% · Quote x{p.quote}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Warm rain (own only) */}
        <div>
          <h2 className="text-lg font-bold text-sky-300 flex items-center gap-2 mb-2"><Cloud size={18} /> Warmer Regen</h2>
          {data.warmRain.length === 0 ? <p className="text-white/40 text-sm">Für dich sind (noch) keine Nachrichten hinterlegt.</p> : (
            <div className="space-y-2">
              {data.warmRain.map((w, i) => (
                <Card key={i} className="bg-gradient-to-br from-sky-900/30 to-indigo-900/10 border-sky-500/20">
                  <CardContent className="p-4 space-y-1">
                    {(w.entries || []).map((e, j) => <p key={j} className="text-white/90 italic">„{e}"</p>)}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
